import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin'
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin'
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin'
import { LexicalEditor } from 'lexical'
import { useRef } from 'react'
import { CLEAR_HISTORY_COMMAND } from 'lexical'

import EditorTheme from '@/components/edit/editor-theme'
import '@/components/edit/editor-theme.css'
import { getEditorNodes, handleExportPDF, cleanEditorState } from '@/components/edit/utils'
import { docs } from '@/services/docs/docs-service'
import { ExtendedSerializedEditorState } from '@interfaces/lexical'
import LoadingIndicator from '@/components/ui/loading-indicator'
import { Card } from '@/components/ui/card'
import toast from 'react-hot-toast'
import { Logo } from '@/components/common/Logo'
import { Button } from '@/components/ui/button'
import { Icon } from '@iconify/react'
import { eventsService } from '@/services/analytics/events-service'
import { EventCategory, EventName } from '@interfaces/events'
import { cn } from '@/lib/utils'
import { DocumentResponse } from '@interfaces/document'
import { useBilling } from '@/hooks/useBilling'
import { SubscriptionStatus } from '@interfaces/auth'

interface IShareLinkDocument {
  senderSubscriptionStatus?: SubscriptionStatus
  editorState: ExtendedSerializedEditorState
  allowDownload: boolean
  documentPublicId: string
  documentName: string
}

function DocumentErrorState() {
  return (
    <div className="bg-radial min-h-screen bg-background bg-lime-50">
      <header className="sticky top-0 z-50 flex h-16 items-center justify-between gap-4 border-b bg-lime-50 px-4 md:px-6">
        <nav className="hidden flex-col gap-6 text-lg font-medium md:flex md:flex-row md:items-center md:gap-5 md:text-sm lg:gap-6">
          <a href="/" className="flex items-center gap-2 text-lg font-semibold md:text-base">
            <Logo className="h-6 w-6" />
            <span className="sr-only">Cobrief</span>
          </a>
        </nav>
      </header>

      <div className="container mx-auto mb-10 mt-8 max-w-4xl">
        <div className="mb-4 text-center text-sm text-gray-500">
          Create your own document{' '}
          <a href="https://platform.cobrief.app/sign-up" className="text-lime-600 hover:underline">
            for free with Cobrief
          </a>
        </div>

        <Card className="p-6">
          <div className="py-8 text-center">
            <h1 className="mb-2 text-xl font-semibold text-red-600">Document Not Found</h1>
            <p className="text-gray-600">The document you're looking for does not exist or is no longer available.</p>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default function ReadonlyDocPage() {
  const { active } = useBilling()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const shareLinkId = searchParams.get('sli')
  const documentId = searchParams.get('id')

  const [loading, setLoading] = useState(true)
  const editorRef = useRef<LexicalEditor | undefined>()
  const [exportLoading, setExportLoading] = useState(false)
  const [currentDocument, setCurrentDocument] = useState<IShareLinkDocument | null>(null)

  async function onExportPDF() {
    if (!currentDocument?.allowDownload) return toast.error('Download is not allowed for this document')

    if (currentDocument?.senderSubscriptionStatus !== 'active')
      return toast.error(
        'Downloads are disabled for this document because it was shared from a trial account. Ask the sender to upgrade to enable downloads.'
      )

    setExportLoading(true)
    await handleExportPDF(editorRef.current)()
    setExportLoading(false)

    // fire recipient_downloaded_doc event
    eventsService.fireEvent({
      name: EventName.RECIPIENT_DOWNLOADED_DOC,
      category: EventCategory.RECIPIENT_ENGAGEMENT,
      doc_public_id: currentDocument?.documentPublicId || undefined,
      source: undefined,
      meta: {},
    })
  }

  // for share link
  const fetchDocumentByShareLinkId = async () => {
    if (!shareLinkId) {
      toast.error('Invalid share link')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const response = await docs.getByShareLinkId(shareLinkId)

      const responseData = response.data

      if (responseData) {
        setCurrentDocument(responseData)

        // fire doc_viewed_by_recipient event
        eventsService.fireEvent({
          name: EventName.DOC_VIEWED_BY_RECIPIENT,
          category: EventCategory.RECIPIENT_ENGAGEMENT,
          doc_public_id: responseData.documentPublicId,
          source: undefined,
          meta: {},
        })
      } else {
        toast.error('Document not found or no longer available')
      }
    } catch (error: any) {
      toast.error(error?.data?.error || 'Failed to load document')
    } finally {
      setLoading(false)
    }
  }

  // for readonly document page
  const fetchDocumentByDocumentId = async () => {
    if (!documentId) {
      toast.error('Invalid document ID')
      return
    }

    setLoading(true)

    let documentResponse = await docs.getContentsByPublicId(documentId)
    let data = documentResponse.data as DocumentResponse
    let editor_state = data.data.editor_state

    // retry if editor_state is not found
    if (!editor_state) {
      await new Promise(resolve => setTimeout(resolve, 2000))

      documentResponse = await docs.getContentsByPublicId(documentId)
      data = documentResponse.data as DocumentResponse
      editor_state = data.data.editor_state

      if (!editor_state) {
        toast.error('Document not found or no longer available')
        setLoading(false)
        return
      }
    }

    setCurrentDocument({
      editorState: editor_state,
      allowDownload: false,
      documentPublicId: documentId,
      documentName: data.data.file_name || 'Untitled Document',
    })

    setLoading(false)
  }

  useEffect(() => {
    // user has active subscription, redirect to review page
    if (active && !shareLinkId) {
      navigate(`/review?id=${documentId}`, { replace: true })
      return
    }

    // user is coming from the review page since they have no active subscription (/readonly?id=...)
    if (location.pathname === '/readonly' && !shareLinkId && documentId) {
      fetchDocumentByDocumentId()
    } else {
      // user is coming from the share link page (/doc?sli=...)
      fetchDocumentByShareLinkId()
    }
  }, [shareLinkId, documentId])

  // Set up editor when editorState is loaded
  useEffect(() => {
    if (!editorRef.current || !currentDocument?.editorState) return

    // Clear history after loading saved state
    editorRef.current.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined)

    // Clean malformed link nodes and parse the editor state
    const cleanedEditorState = cleanEditorState(currentDocument.editorState)
    const parsedEditorState = editorRef.current.parseEditorState(cleanedEditorState)
    editorRef.current.setEditorState(parsedEditorState)
  }, [currentDocument?.editorState])

  if (loading) {
    return <LoadingIndicator label="Loading Document" />
  }

  if (!currentDocument) {
    return <DocumentErrorState />
  }

  return (
    <div className="bg-radial min-h-screen bg-background bg-lime-50">
      {shareLinkId && (
        <header className="sticky top-0 z-50 flex h-16 items-center justify-between gap-4 border-b bg-lime-50 px-4 md:px-6">
          <nav className="hidden flex-col gap-6 text-lg font-medium md:flex md:flex-row md:items-center md:gap-5 md:text-sm lg:gap-6">
            <a href="/" className="flex items-center gap-2 text-lg font-semibold md:text-base">
              <Logo className="h-6 w-6" />
              <span className="sr-only">Cobrief</span>
            </a>
          </nav>

          <p className="text-sm text-default-600">{currentDocument.documentName}</p>

          <Button
            variant="accentGreen"
            className={cn('flex items-center gap-2', !currentDocument.allowDownload && 'invisible')}
            onClick={onExportPDF}
            disabled={exportLoading || currentDocument.senderSubscriptionStatus !== 'active'}
            title={
              currentDocument.senderSubscriptionStatus !== 'active'
                ? 'Downloads are disabled for this document because it was shared from a trial account. Ask the sender to upgrade to enable downloads.'
                : undefined
            }
          >
            <Icon icon="proicons:pdf-2" className="h-5 w-5" />
            {exportLoading ? 'Exporting...' : 'Download PDF'}
          </Button>
        </header>
      )}

      <div className="container mx-auto mb-10 mt-8 max-w-4xl">
        {shareLinkId && (
          <div className="mb-4 text-center text-sm text-gray-500">
            Create your own document{' '}
            <a href="https://platform.cobrief.app/sign-up" className="text-lime-600 hover:underline">
              for free with Cobrief
            </a>
          </div>
        )}

        <Card className="p-3">
          <LexicalComposer
            initialConfig={{
              namespace: 'ReadonlyEditor',
              theme: EditorTheme,
              editable: false, // Readonly mode
              onError: (error: Error) => {
                console.error('Editor error:', error)
              },
              nodes: getEditorNodes(),
            }}
          >
            <div className="min-h-[500px]">
              <EditorRefPlugin editorRef={editorRef} />
              <RichTextPlugin
                contentEditable={
                  <ContentEditable className="relative h-full min-h-[400px] flex-1 resize-none overflow-y-auto rounded-lg p-[15px] text-base caret-black outline-none [tab-size:1]" />
                }
                placeholder={
                  <div className="pointer-events-none absolute left-[15px] top-[15px] inline-block select-none overflow-hidden text-ellipsis text-base text-[#999]">
                    Document content will appear here
                  </div>
                }
                ErrorBoundary={LexicalErrorBoundary as any}
              />
              <HistoryPlugin />
              <AutoFocusPlugin />
              <ListPlugin />
              <LinkPlugin />
              <TabIndentationPlugin />
            </div>
          </LexicalComposer>
        </Card>
      </div>
    </div>
  )
}
